import type { FC, ReactElement } from 'react'

import tw, { styled } from 'twin.macro'
import Head from 'next/head'
import Link from 'next/link'

import AppLayout from '@/providers/AppLayout'
import BaseuiProvider from '@/providers/baseui'
import StaticLayout from '@/components/StaticLayout'

import svgPlusSign from '../lib/svgs/plusSign.svg'
import IconTwitter from '../lib/svgs/twitter.svg'
import iconLink from '../lib/svgs/link.svg'
import { Avatar } from '@chakra-ui/react'

const HeaderContainer = tw.header`w-full px-4 md:px-[8vw] pt-[12.5vw] pb-[8.75vw] relative z-10`

const PageTitle = tw.h1`text-5xl md:text-[80px] font-secondary text-white uppercase`

const Brief = styled.div`
  ${tw`text-sm font-primary mt-4 pb-4 md:mt-14 md:pb-9 relative`}

  &:after {
    ${tw`h-1 w-[12.7vw] bg-brand absolute bottom-0 left-0`}
    content: "";
  }
`

const PageHeading: React.FC = () => {
  return (
    <HeaderContainer>
      <PageTitle>Team</PageTitle>
      <Brief>
        Bringing power to the people by creating an intergalactic metaverse with
        next-gen technologies.
      </Brief>
    </HeaderContainer>
  )
}

const SectionContainer = tw.div`flex flex-col justify-end my-16`

const SectionHeaderLine1 = styled.div`
  ${tw`text-3xl font-bold font-secondary flex flex-row items-end uppercase`}

  &:before {
    ${tw`h-[131px] w-[1px] bg-brand relative -left-5`}
    content: "";
  }
`

const StyledSvgPlugSign = tw(svgPlusSign)`ml-4 mb-2`

const SectionHeaderLine2 = tw.div`mt-4 font-primary`

const SectionHeader: FC<{
  title: string
  brief: string
}> = ({title, brief}) => {
  return (
    <SectionContainer>
      <SectionHeaderLine1>
        <h2>{title}</h2>
        <StyledSvgPlugSign />
      </SectionHeaderLine1>
      <SectionHeaderLine2>{brief}</SectionHeaderLine2>
    </SectionContainer>
  )
}

const Section = tw.section`px-5`

const CellContainer = tw.div`flex flex-row py-8 border-b border-dashed border-[#8c8c8c] md:max-w-[50vw]`

const CellLeft = tw.div`flex flex-col w-16 md:w-auto md:min-w-[9.95vw] mr-10`

const CoreTeamMemberName = tw.div`font-secondary text-white text-base font-bold mb-2`

const CoreTeamMemberTitle = tw.div`font-primary text-brand`

const CoreTeamMemberBody = tw.div`font-primary text-sm leading-6 flex-grow`

const CoreTeamCell: FC<{
  name: string
  title: string
  children: string
}> = ({name, title, children}) => {
  return (
    <CellContainer>
      <CellLeft>
        <CoreTeamMemberName>{name}</CoreTeamMemberName>
        <CoreTeamMemberTitle>{title}</CoreTeamMemberTitle>
      </CellLeft>
      <CoreTeamMemberBody>{children}</CoreTeamMemberBody>
    </CellContainer>
  )
}

const TeamMemberCard = ({ name, title, twitter, shellNftId, children }: {
  name: string
  title: string
  twitter?: string
  shellNftId?: number
  children: React.ReactNode
}) => {
  return (
    <div css={[tw`flex flex-row justify-between gap-2 p-5 bg-[rgba(22,27,31,0.8)] rounded`, 'aspect-ratio: 23/15; backdrop-filter: blur(10px);']}>
      <div tw='shrink flex flex-col justify-between'>
        <main tw='flex flex-col gap-2.5'>
          <header tw='flex flex-col gap-0.5'>
            <h4 tw='text-base font-secondary font-bold text-white'>{name}</h4>
            <div tw='text-sm font-medium text-[#36FAD7]'>{title}</div>
          </header>
          <p tw='text-sm text-grey-500'>{children}</p>
        </main>
        {twitter ? (
          <footer tw='flex flex-row items-center gap-1.5'>
            <IconTwitter tw="h-4 w-4 text-grey-300" />
            <Link href={`https://twitter.com/${twitter}`} target="_blank" tw='text-sm text-grey-300 hover:underline hover:text-white'>
              @{twitter}
            </Link>
          </footer>
        ) : null}
      </div>
      {shellNftId ? (
        <div tw='min-w-[5rem] w-20 h-20 rounded-full overflow-hidden'>
          <img src={`https://img0.phala.world/renderer/cb:0118/avatar/2/${shellNftId}.jpg`} alt={name} tw='w-full h-full' />
        </div>
      ) : null}
    </div>
  )
}

const CoreTeam = () => {
  return (
    <Section tw='max-w-[840px]'>
      <SectionHeader title="Core Team" brief="our team" />
      <main tw='grid grid-cols-2 grid-flow-row gap-4'>
        <TeamMemberCard name="Marvin Tong" title="CEO / Co-founder" shellNftId={109} twitter="marvin_tong">
          Co-founder of Phala Network
        </TeamMemberCard>
        <TeamMemberCard name="Dan Quirk" title="CPO" shellNftId={290} twitter="kureus_">
          Deeply interested in a trustless first world. Living in the metaverse. Startup Addict.
        </TeamMemberCard>
        <TeamMemberCard name="Xiaolu Huang" title="PhalaWorld Director" shellNftId={286} twitter="littlekiradeer">
          Ex-Senior Product Manager in Didi, Baidu and Bytedance.
        </TeamMemberCard>
        <TeamMemberCard name="Leechael Yan" title="Engineer" shellNftId={283}>
        </TeamMemberCard>
        <TeamMemberCard name="Joshua Waller" title="Dev Advocate/dApp Dev" shellNftId={287} twitter="hashwarlock">
          Dev. advocate of Phala Network, used to work for ForeScout Technologies Inc. and Dell.
        </TeamMemberCard>
        <TeamMemberCard name="Jojo Zhou" title="Global Marketing Manager" shellNftId={289}>
          Graduated from Ohio State University dual degree in Finance & Communications, Influencer on RED with over 100k followers.
        </TeamMemberCard>
        <TeamMemberCard name="Lucia Guo" title="COO PhalaWorld" shellNftId={1098} twitter="LuciaGuo4">
          Searching for precious in the broken world #phalaworld.
        </TeamMemberCard>
        <TeamMemberCard name="William Zhou" title="Design Director" shellNftId={285} twitter="Williamty_zhou">
          Senior designer and game designer, former design director at ChainNews, former design director at Fundamental Labs, with an independent design studio.
        </TeamMemberCard>
        <TeamMemberCard name="Eric Ren" title="Graphic Designer" twitter="Ren1Eric">
          Visual designer for Phala Network, formerly with a public company.
        </TeamMemberCard>
      </main>
    </Section>
  )
}

export const LogoGrid = tw.div`grid grid-cols-1 lg:grid-cols-2 lg:gap-5 xl:grid-cols-3 md:gap-20 w-full`

const LogoCellContainer = tw.div`grid-cols-1 mb-8`

const Headline = tw.div`flex flex-row justify-between items-center pb-2 border-b border-solid border-brand`

const CellPartnerName = tw.h4`font-secondary text-white text-base`

const CellPartnerTwitterIcon = tw(
  IconTwitter
)`h-4 w-5 mr-2 text-white transition-colors hover:text-brand`

const CellPartnerLinkIcon = tw(
  iconLink
)`h-4 w-5 mr-2 text-white transition-colors hover:text-brand`

const CellPartnerTwitterLink = tw.a`font-primary text-white text-sm underline transition-colors hover:text-brand`

const CellPartnerLogo = tw.img`rounded-[100%] w-36 mt-5`

export const LogoCell: FC<{
  name: string
  twitterId?: string
  link?: string
  imageUrl: string
}> = ({name, twitterId, imageUrl, link}) => {
  return (
    <LogoCellContainer>
      <Headline>
        <CellPartnerName>{name}</CellPartnerName>
        <div>
          {twitterId && (
            <CellPartnerTwitterLink
              href={`https://twitter.com/${twitterId}`}
              target="_blank"
              rel="noreferrer"
            >
              <CellPartnerTwitterIcon />
            </CellPartnerTwitterLink>
          )}
          {link && (
            <CellPartnerTwitterLink
              href={link}
              target="_blank"
              rel="noreferrer"
            >
              <CellPartnerLinkIcon />
            </CellPartnerTwitterLink>
          )}
        </div>
      </Headline>
      <CellPartnerLogo src={imageUrl} alt={name} />
    </LogoCellContainer>
  )
}

const Partner = () => {
  return (
    <Section>
      <SectionHeader title="Partner" brief="our team" />
      <main tw="flex flex-row md:w-[50vw]">
        <LogoGrid>
          <LogoCell
            name="RMRK"
            twitterId="RmrkApp"
            imageUrl="/static/images/teamPage/logo-rmrk.jpg"
          />
          <LogoCell
            name="Bit.Country"
            twitterId="BitDotCountry"
            imageUrl="/static/images/teamPage/logo-bitcountry.jpg"
          />
          <LogoCell
            name="My Crypto Profile"
            twitterId="Web3Mcp"
            imageUrl="/static/images/teamPage/logo-mcp.jpg"
          />
          <LogoCell
            name="Talisman"
            twitterId="wearetalisman"
            imageUrl="/static/images/teamPage/logo-talisman.jpg"
          />
          <LogoCell
            name="Animoca"
            twitterId="animocabrands"
            imageUrl="/static/images/teamPage/logo-animoca.jpg"
          />
          <LogoCell
            name="HYPERSPHERE"
            twitterId="hypersphere_"
            imageUrl="/static/images/teamPage/logo-hypersphere.jpg"
          />
          <LogoCell
            name="IOSG"
            twitterId="IOSGVC"
            imageUrl="/static/images/teamPage/logo-iosg.jpg"
          />
          <LogoCell
            name="DFG"
            twitterId="DFG_Official"
            imageUrl="/static/images/teamPage/logo-dfg.jpg"
          />
          <LogoCell
            name="INCUBA ALPHA"
            twitterId="Incuba_Alpha"
            imageUrl="/static/images/teamPage/logo-incuba-alpha.jpg"
          />
          <LogoCell
            name="PAKA"
            twitterId="pakafund"
            imageUrl="/static/images/teamPage/logo-paka.jpg"
          />
        </LogoGrid>
      </main>
    </Section>
  )
}

const CoArtist = () => {
  return (
    <Section>
      <SectionHeader title="CO-ARTIST" brief="our team" />
      <main tw="flex flex-row md:w-[50vw] mb-[11vw]">
        <LogoGrid>
          <LogoCell
            name="ZHONGENHUI"
            imageUrl="/static/images/teamPage/logo-zhongenhui.jpg"
          />
          <LogoCell
            name="SKYLAB"
            twitterId="SkyLabsCorp"
            imageUrl="/static/images/teamPage/logo-skylab.jpg"
          />
          <LogoCell
            name="BowserStaxx"
            twitterId="BowserStaxx"
            imageUrl="/static/images/teamPage/logo-bowserstaxx.jpg"
          />
          <LogoCell
            name="YUANGUI"
            link="https://www.artstation.com/yuangui7"
            imageUrl="/static/images/teamPage/logo-yuangui.jpg"
          />
          <LogoCell
            name="OneDreamTenyrs"
            twitterId="OneDreamTenyrs"
            imageUrl="/static/images/teamPage/logo-tenyrs.jpg"
          />
        </LogoGrid>
      </main>
    </Section>
  )
}

const CentralContaienr = tw.div`flex flex-col items-center relative z-10`

const BackgroundIllustraction = styled.div`
  ${tw`absolute top-0 right-0 select-none pointer-events-none z-0 md:w-[62.96875vw] md:h-[51.5625vw]`}
  background: url('/static/images/teamPage/illustraction.png') no-repeat 100% 0;
  background-size: 100%;
`

export default function TeamPage() {
  return (
    <BaseuiProvider>
      <Head>
        <title>Team | PhalaWorld</title>
      </Head>
      <StaticLayout>
        <PageHeading />
        <div tw="absolute top-0 left-0 z-0 md:h-[51.5625vw] w-full">
          <BackgroundIllustraction />
        </div>
        <CentralContaienr>
          <CoreTeam />
          <Partner />
          <CoArtist />
        </CentralContaienr>
      </StaticLayout>
    </BaseuiProvider>
  )
}

TeamPage.getLayout = (page: ReactElement) => {
  return (
    <AppLayout>
      {page}
    </AppLayout>
  )
}
